import React, { useRef, useState } from 'react';
import { Box, Button, Checkbox, FormControlLabel, Stack, TextField, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useToast } from '../../contexts/ToastContext';
import { storeProductUploadApi, type MyStoreProduct, type StoreProductUploadStatus } from '../../services/storeProductUploadApi';
import {
  ProductSubmission,
  ProductSubmissionResult,
  ProductSubmissionScope,
  ProductSubmissionStatus,
  productSubmissionsApi,
} from '../../services/productSubmissionsApi';
import { tokens } from '../../theme/tokens';
import { AdminProductDetailDialog } from '../admin/productDetail/AdminProductDetailDialog';
import type { DraftSubmission } from '../admin/productDetail/productDraft';
import { InlineMessage, LoadingState, SectionCard, StatusChip } from '../ui';
import type { StatusTone } from '../ui';

interface ProductSubmissionsCardProps {
  scope: ProductSubmissionScope;
  /** Store only: ids already in the selection the page shows, so a submitted product reads In sync selection at once. */
  selectedIds?: Set<string>;
  /**
   * Opens a product in the product browser's own details dialog (MOT-695): the
   * provisional product while it is open, the catalog product once promoted.
   * Without it, View details opens the product editor's read view.
   */
  onOpenProduct?: (productId: string) => void;
  /**
   * Store only, flag `store_product_upload`: offers "Keep this private to my
   * store", which adds the product through the store upload instead (never
   * sent to Motzu) and lists those products below the submissions.
   */
  privateUploadEnabled?: boolean;
}

const ROW_TONE: Record<StoreProductUploadStatus, StatusTone> = {
  matched: 'info',
  created: 'positive',
  updated: 'info',
  rejected: 'problem',
};

export const SUBMISSION_TONE: Record<ProductSubmissionStatus, StatusTone> = {
  in_review: 'info',
  needs_answer: 'warning',
  approved: 'positive',
  promoted: 'positive',
  rejected: 'problem',
};

/** A store can still add a submission to its sync, and edit it, until Motzu promotes or rejects it. */
const canAddToSync = (submission: ProductSubmission) => submission.status !== 'promoted' && submission.status !== 'rejected';
const canEdit = canAddToSync;

const THUMB = 48;

const SubmissionThumb: React.FC<{ submission: ProductSubmission; size?: number; label: string }> = ({ submission, size = THUMB, label }) => {
  const url = submission.imageUrls[0];
  return (
    <Box
      sx={{
        width: size,
        height: size,
        flex: '0 0 auto',
        borderRadius: 1,
        border: `1px solid ${tokens.line.hairline}`,
        bgcolor: 'background.default',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      {url ? (
        <Box component="img" src={url} alt={submission.name} loading="lazy" sx={{ width: '100%', height: '100%', objectFit: 'contain' }} />
      ) : (
        <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', px: 0.5, lineHeight: 1.1 }}>{label}</Typography>
      )}
    </Box>
  );
};

/**
 * "Add missing product" on the product browser (Jordan, store shell, and
 * Priya, vendor shell; flag `product_submissions`). A store or vendor submits
 * a product the catalog does not carry: it becomes a provisional product only
 * the submitter sees (a store can sync it at once) and a QA record Motzu
 * enriches, verifies and promotes. The list below shows each submission's
 * status and Motzu's enrichment questions, answered inline. One product is
 * entered in the shared product editor's create mode (MOT-701), the same
 * editor that edits a submission afterwards, so everything the submitter knows
 * goes in at once.
 */
const ProductSubmissionsCard: React.FC<ProductSubmissionsCardProps> = ({ scope, selectedIds, onOpenProduct, privateUploadEnabled = false }) => {
  const { t } = useAppTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState<ProductSubmissionResult | null>(null);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  // The submission open in the product editor (MOT-695).
  const [editorId, setEditorId] = useState<string | null>(null);
  const offerPrivate = scope === 'store' && privateUploadEnabled;
  const [keepPrivate, setKeepPrivate] = useState(false);
  const toPrivate = offerPrivate && keepPrivate;
  const [resultPrivate, setResultPrivate] = useState(false);

  const listKey = ['product-submissions', scope];
  const { data: submissions, isLoading } = useQuery({
    queryKey: listKey,
    queryFn: () => productSubmissionsApi.list(scope),
  });

  // Products the store added and kept private (never sent to Motzu): the store upload's own list, less the submitted ones.
  const { data: uploaded } = useQuery({
    queryKey: ['store-uploaded-products'],
    queryFn: () => storeProductUploadApi.listMine(),
    enabled: offerPrivate,
  });
  const keptPrivate: MyStoreProduct[] = (uploaded || []).filter((product) => !product.submissionId);

  const onResult = (data: ProductSubmissionResult) => {
    setResult(data);
    queryClient.invalidateQueries({ queryKey: listKey });
    queryClient.invalidateQueries({ queryKey: ['store-uploaded-products'] });
  };
  const onError = (error: unknown) => showToast(error instanceof Error ? error.message : t('store.productSubmissions.error'), 'error');

  const submitCsv = useMutation({
    mutationFn: async (file: File): Promise<ProductSubmissionResult> => {
      setResultPrivate(toPrivate);
      return toPrivate ? { ...(await storeProductUploadApi.uploadCsv(file)), submitted: 0 } : productSubmissionsApi.submitCsv(scope, file);
    },
    onSuccess: onResult,
    onError,
  });

  /**
   * The create dialog's submit: the row plus the editor's details, or, kept
   * private, the plain upload row. A rejected row keeps the dialog open with
   * its reason; anything else closes it and shows the result below.
   */
  const submitDraft = async ({ row, details, privateRow }: DraftSubmission) => {
    const data: ProductSubmissionResult = toPrivate
      ? { ...(await storeProductUploadApi.createOne(privateRow)), submitted: 0 }
      : await productSubmissionsApi.submitOne(scope, row, details);
    setResultPrivate(toPrivate);
    onResult(data);
    if (data.rejected > 0) throw new Error(data.rows[0]?.reason || t('store.productSubmissions.error'));
    setCreating(false);
    // A catalog match creates nothing; the result list below says so.
    if (toPrivate ? data.created > 0 : data.submitted > 0) {
      showToast(toPrivate ? t('store.productSubmissions.savedPrivate') : t('store.productSubmissions.submitted'), 'success');
    }
  };

  const answer = useMutation({
    mutationFn: ({ submissionId, questionId, text }: { submissionId: string; questionId: string; text: string }) =>
      productSubmissionsApi.answer(scope, submissionId, questionId, text),
    onSuccess: (_data, variables) => {
      setAnswers((prev) => ({ ...prev, [variables.questionId]: '' }));
      queryClient.invalidateQueries({ queryKey: listKey });
      showToast(t('store.productSubmissions.answered'), 'success');
    },
    onError,
  });

  const addToSync = useMutation({
    mutationFn: (ids: string[]) => productSubmissionsApi.select(ids),
    onSuccess: (data, ids) => {
      queryClient.invalidateQueries({ queryKey: ['store-sync-selection'] });
      showToast(
        ids.length > 1 ? t('store.productSubmissions.addedAll', { count: data.added.length }) : t('store.productUpload.added'),
        'success',
      );
    },
    onError,
  });

  const addPrivateToSync = useMutation({
    mutationFn: (id: string) => storeProductUploadApi.select([id]),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['store-sync-selection'] });
      showToast(t('store.productUpload.added'), 'success');
    },
    onError,
  });

  const downloadTemplate = async () => {
    try {
      await productSubmissionsApi.downloadTemplate(scope);
    } catch (error) {
      onError(error);
    }
  };

  const busy = submitCsv.isPending;
  const rows: ProductSubmission[] = submissions || [];
  const unselected = scope === 'store'
    ? rows.filter((s) => canAddToSync(s) && !(selectedIds?.has(s.provisionalProductId) ?? false)).map((s) => s.provisionalProductId)
    : [];
  const editing = rows.find((s) => s.id === editorId) || null;
  const viewDetails = (submission: ProductSubmission) => {
    const productId = submission.status === 'promoted' && submission.promotedCanonicalId ? submission.promotedCanonicalId : submission.provisionalProductId;
    if (onOpenProduct && submission.status !== 'rejected') onOpenProduct(productId);
    else setEditorId(submission.id);
  };

  return (
    <SectionCard title={t('store.productSubmissions.title')} subtitle={t(`store.productSubmissions.subtitle.${scope}`)} dense padded>
      <InlineMessage severity="info">{t(`store.productSubmissions.privacy.${scope}`)}</InlineMessage>

      <Stack direction="row" spacing={1} useFlexGap sx={{ my: 2, flexWrap: 'wrap' }}>
        <Button size="small" variant="outlined" onClick={downloadTemplate}>{t('store.productUpload.template')}</Button>
        <Button size="small" variant="outlined" disabled={busy} onClick={() => fileInput.current?.click()}>
          {t('store.productUpload.uploadCsv')}
        </Button>
        <Button size="small" variant="contained" onClick={() => setCreating(true)}>
          {t('store.productSubmissions.addOne')}
        </Button>
        <input
          ref={fileInput}
          type="file"
          accept=".csv,text/csv"
          hidden
          data-testid="product-submission-csv"
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = '';
            if (file) submitCsv.mutate(file);
          }}
        />
      </Stack>

      {offerPrivate && (
        <FormControlLabel
          sx={{ mt: -1, mb: 1.5 }}
          control={<Checkbox size="small" checked={keepPrivate} onChange={(event) => setKeepPrivate(event.target.checked)} />}
          label={
            <Box>
              <Typography variant="body2">{t('store.productSubmissions.keepPrivate')}</Typography>
              <Typography variant="caption" color="text.secondary">{t('store.productSubmissions.keepPrivateHint')}</Typography>
            </Box>
          }
        />
      )}

      {busy && <LoadingState />}

      {result && (
        <Box sx={{ mb: 2 }} data-testid="product-submission-result">
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {resultPrivate
              ? t('store.productSubmissions.summaryPrivate', { created: result.created, matched: result.matched, rejected: result.rejected })
              : t('store.productSubmissions.summary', { submitted: result.submitted, matched: result.matched, rejected: result.rejected })}
          </Typography>
          {result.pricing.status === 'applied' && (
            <InlineMessage severity="success">{t('store.productUpload.pricingApplied', { count: result.pricing.rows })}</InlineMessage>
          )}
          {result.pricing.status === 'failed' && (
            <InlineMessage severity="warning">{t('store.productUpload.pricingFailed', { message: result.pricing.message || '' })}</InlineMessage>
          )}
          {result.rows.map((row) => (
            <Box
              key={row.row}
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, borderTop: `1px solid ${tokens.line.hairline}`, flexWrap: 'wrap' }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 48 }}>
                {t('store.productUpload.row', { row: row.row })}
              </Typography>
              <Box sx={{ flex: 1, minWidth: 180 }}>
                <Typography variant="body2">{[row.brand, row.model].filter(Boolean).join(' ') || '-'}</Typography>
                {(row.reason || row.warnings.length > 0 || row.matchedBy) && (
                  <Typography variant="caption" color="text.secondary">
                    {[row.reason, row.matchedBy ? t(`store.productUpload.matchedBy.${row.matchedBy}`) : null, ...row.warnings]
                      .filter(Boolean)
                      .join(' · ')}
                  </Typography>
                )}
              </Box>
              <StatusChip
                label={row.submissionId ? t('store.productSubmissions.rowSubmitted') : t(`store.productUpload.status.${row.status}`)}
                tone={ROW_TONE[row.status]}
                size="small"
              />
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
        <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>{t('store.productSubmissions.listTitle')}</Typography>
        {unselected.length > 1 && (
          <Button size="small" variant="contained" disabled={addToSync.isPending} onClick={() => addToSync.mutate(unselected)}>
            {t('store.productSubmissions.addAll', { count: unselected.length })}
          </Button>
        )}
      </Box>
      {isLoading && <LoadingState />}
      {!isLoading && rows.length === 0 && (
        <Typography variant="caption" color="text.secondary">{t('store.productSubmissions.listEmpty')}</Typography>
      )}
      {rows.map((submission) => {
        const selected = selectedIds?.has(submission.provisionalProductId) ?? false;
        const open = submission.questions.filter((q) => !q.answer);
        return (
          <Box key={submission.id} sx={{ py: 1, borderTop: `1px solid ${tokens.line.hairline}` }} data-testid="product-submission-row">
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <SubmissionThumb submission={submission} label={t('store.productSubmissions.noImage')} />
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>{submission.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {[submission.brand, submission.model, submission.sku ? `SKU ${submission.sku}` : null, submission.upc ? `UPC ${submission.upc}` : null]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
                {submission.status === 'rejected' && submission.reviewNote && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{submission.reviewNote}</Typography>
                )}
              </Box>
              <StatusChip label={t(`store.productSubmissions.status.${submission.status}`)} tone={SUBMISSION_TONE[submission.status]} size="small" />
              <Button
                size="small"
                variant="text"
                aria-label={`${t('store.productSubmissions.viewDetails')}: ${submission.name}`}
                onClick={() => viewDetails(submission)}
              >
                {t('store.productSubmissions.viewDetails')}
              </Button>
              {canEdit(submission) && (
                <Button
                  size="small"
                  variant="text"
                  aria-label={`${t('store.productSubmissions.editDetails')}: ${submission.name}`}
                  onClick={() => setEditorId(submission.id)}
                >
                  {t('store.productSubmissions.editDetails')}
                </Button>
              )}
              {scope === 'store' && canAddToSync(submission) && (
                selected ? (
                  <StatusChip label={t('store.productUpload.selected')} tone="positive" size="small" />
                ) : (
                  <Button size="small" variant="outlined" disabled={addToSync.isPending} onClick={() => addToSync.mutate([submission.provisionalProductId])}>
                    {t('store.productUpload.addToSync')}
                  </Button>
                )
              )}
            </Box>
            {open.map((question) => (
              <Box key={question.id} sx={{ mt: 1, pl: { sm: 2 } }}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>{t('store.productSubmissions.question', { question: question.question })}</Typography>
                <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
                  <TextField
                    size="small"
                    sx={{ flex: 1, minWidth: 220 }}
                    placeholder={t('store.productSubmissions.answerPlaceholder')}
                    value={answers[question.id] || ''}
                    onChange={(event) => setAnswers((prev) => ({ ...prev, [question.id]: event.target.value }))}
                  />
                  <Button
                    size="small"
                    variant="contained"
                    disabled={answer.isPending || !(answers[question.id] || '').trim()}
                    onClick={() => answer.mutate({ submissionId: submission.id, questionId: question.id, text: (answers[question.id] || '').trim() })}
                  >
                    {t('store.productSubmissions.answer')}
                  </Button>
                </Stack>
              </Box>
            ))}
          </Box>
        );
      })}

      {offerPrivate && keptPrivate.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>{t('store.productSubmissions.privateListTitle')}</Typography>
          {keptPrivate.map((product) => {
            const selected = selectedIds?.has(product.id) ?? false;
            return (
              <Box key={product.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1, borderTop: `1px solid ${tokens.line.hairline}`, flexWrap: 'wrap' }} data-testid="product-private-row">
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{product.name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {[product.brand, product.model, product.sku ? `SKU ${product.sku}` : null].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
                <StatusChip label={t('store.productSubmissions.keptPrivate')} tone="neutral" size="small" />
                {onOpenProduct && (
                  <Button size="small" variant="text" aria-label={`${t('store.productSubmissions.viewDetails')}: ${product.name}`} onClick={() => onOpenProduct(product.id)}>
                    {t('store.productSubmissions.viewDetails')}
                  </Button>
                )}
                {selected ? (
                  <StatusChip label={t('store.productUpload.selected')} tone="positive" size="small" />
                ) : (
                  <Button size="small" variant="outlined" disabled={addPrivateToSync.isPending} onClick={() => addPrivateToSync.mutate(product.id)}>
                    {t('store.productUpload.addToSync')}
                  </Button>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      {creating && (
        <AdminProductDetailDialog
          productId={null}
          create={{
            submitLabel: toPrivate ? t('store.productSubmissions.savePrivate') : t('store.productSubmissions.submit'),
            priceNote: toPrivate ? t('store.productSubmissions.priceNote.private') : t(`store.productSubmissions.priceNote.${scope}`),
            skuHint: t(`store.productSubmissions.skuHint.${scope}`),
            notice: toPrivate ? <InlineMessage severity="info">{t('store.productSubmissions.privateDetailsNote')}</InlineMessage> : undefined,
            onSubmit: submitDraft,
          }}
          onClose={() => setCreating(false)}
          onChanged={() => queryClient.invalidateQueries({ queryKey: listKey })}
          onDeleted={() => setCreating(false)}
        />
      )}

      {editing && (
        <AdminProductDetailDialog
          productId={editing.provisionalProductId}
          submitter={{ scope, submissionId: editing.id, statusLabel: t(`store.productSubmissions.status.${editing.status}`) }}
          onClose={() => setEditorId(null)}
          onChanged={() => queryClient.invalidateQueries({ queryKey: listKey })}
          onDeleted={() => setEditorId(null)}
        />
      )}
    </SectionCard>
  );
};

/** The chip on the picker's filter row that opens the section. */
export const ProductSubmissionsToggle: React.FC<{ open: boolean; onToggle: () => void }> = ({ open, onToggle }) => {
  const { t } = useAppTranslation();
  return (
    <Button
      size="small"
      variant={open ? 'contained' : 'outlined'}
      onClick={onToggle}
      aria-pressed={open}
      sx={{ height: 38, whiteSpace: 'nowrap' }}
    >
      {t('store.productSubmissions.title')}
    </Button>
  );
};

export default ProductSubmissionsCard;
